import api from './api'
import type { PageResponse } from '@/types/comum'
import type {
  ConfiguracaoPrivacidade,
  Expurgo,
  FiltroPrevia,
  NovaPoliticaRetencaoRequest,
  NovaRetencaoLegalRequest,
  NovoExpurgoRequest,
  PoliticaRetencao,
  PoliticasRetencao,
  PreviaExpurgo,
  RegistroExpurgo,
  RetencaoLegal,
  AlvoRetencaoLegal,
  ReaplicacaoExpurgoResponse,
  StatusExpurgo,
} from '@/types/privacidade'

/**
 * `originei-documentos` — `/api/v1/privacidade/**` (AN-12 §7.3; contrato REAL do DEV-W, sha
 * edb521a; `reaplicar` do DEV-X). Tudo back-office global: ADMIN e DPO; demais ⇒ 403. Sucesso
 * devolve o DTO direto; erros `{ success:false, message, errorCode, data? }`. Se o backend mudar
 * um nome, ajuste AQUI (e em `types/privacidade.ts`), não nas telas.
 */
const BASE = '/api/v1/privacidade'

/**
 * `GET /politicas-retencao` devolve o histórico inteiro, cada versão com `vigente` marcado pelo
 * SERVIDOR. A tela só separa pela marca — nada de calcular qual é a vigente aqui.
 */
export const normalizarPoliticas = (resposta: PoliticaRetencao[] | null | undefined): PoliticasRetencao => {
  const lista = resposta ?? []
  return {
    vigentes: lista.filter((p) => p.vigente === true),
    historico: lista.filter((p) => p.vigente !== true),
  }
}

/** `?documentoIds=a&documentoIds=b` (repetido — o axios mandaria `documentoIds[]`) ou `?reservaId=`. */
const queryPrevia = (f: FiltroPrevia): string => {
  const q = new URLSearchParams()
  ;(f.documentoIds ?? []).forEach((id) => q.append('documentoIds', id))
  if (f.reservaId) q.append('reservaId', f.reservaId)
  return q.toString()
}

export interface FiltroExpurgos {
  status?: StatusExpurgo
  solicitacao?: string
  page?: number
  size?: number
}

/** Listagens podem vir paginadas (`PageResponse`) ou como lista — a tela recebe lista. */
const comoLista = <T>(r: PageResponse<T> | T[] | null | undefined): T[] =>
  !r ? [] : Array.isArray(r) ? r : (r.content ?? [])

export const privacidadeService = {
  configuracao: async (): Promise<ConfiguracaoPrivacidade> =>
    api.get<ConfiguracaoPrivacidade>(`${BASE}/configuracao`),

  // ---------------------------------------------------------- política de retenção
  politicas: async (): Promise<PoliticasRetencao> =>
    normalizarPoliticas(await api.get<PoliticaRetencao[]>(`${BASE}/politicas-retencao`)),

  /** Só DPO. Alterar = nova versão; a anterior fica (append-only). */
  novaPolitica: async (req: NovaPoliticaRetencaoRequest): Promise<PoliticaRetencao> =>
    api.post<PoliticaRetencao>(`${BASE}/politicas-retencao`, req),

  // ---------------------------------------------------------- legal hold
  retencoesLegais: async (filtro: { alvoTipo?: AlvoRetencaoLegal; ativas?: boolean } = {}): Promise<RetencaoLegal[]> => {
    const params: Record<string, string> = {}
    if (filtro.alvoTipo) params.alvoTipo = filtro.alvoTipo
    if (filtro.ativas !== undefined) params.ativas = String(filtro.ativas)
    return comoLista(
      await api.get<PageResponse<RetencaoLegal> | RetencaoLegal[]>(`${BASE}/retencoes-legais`, { params })
    )
  },

  /**
   * DPO ou ADMIN. Hold com expurgos abertos sobre o alvo os passa para BLOQUEADO_RETENCAO — a
   * resposta traz `expurgosBloqueados` (códigos EXP-…). 409 RETENCAO_LEGAL_JA_ATIVA.
   */
  criarRetencaoLegal: async (req: NovaRetencaoLegalRequest): Promise<RetencaoLegal> =>
    api.post<RetencaoLegal>(`${BASE}/retencoes-legais`, req),

  /** Só DPO, motivo obrigatório (vai para `motivo_liberacao`). 409 RETENCAO_LEGAL_JA_LIBERADA. */
  liberarRetencaoLegal: async (id: string, motivo: string): Promise<RetencaoLegal> =>
    api.post<RetencaoLegal>(`${BASE}/retencoes-legais/${encodeURIComponent(id)}/liberar`, { motivo }),

  // ---------------------------------------------------------- fila de expurgo
  previa: async (filtro: FiltroPrevia): Promise<PreviaExpurgo> =>
    api.get<PreviaExpurgo>(`${BASE}/expurgos/previa?${queryPrevia(filtro)}`),

  criarExpurgo: async (req: NovoExpurgoRequest): Promise<Expurgo> =>
    api.post<Expurgo>(`${BASE}/expurgos`, req),

  expurgos: async (f: FiltroExpurgos = {}): Promise<Expurgo[]> => {
    const params: Record<string, string | number> = { page: f.page ?? 0, size: f.size ?? 50 }
    if (f.status) params.status = f.status
    if (f.solicitacao) params.solicitacao = f.solicitacao
    return comoLista(await api.get<PageResponse<Expurgo> | Expurgo[]>(`${BASE}/expurgos`, { params }))
  },

  expurgo: async (id: string): Promise<Expurgo> =>
    api.get<Expurgo>(`${BASE}/expurgos/${encodeURIComponent(id)}`),

  /**
   * 1ª só DPO, 2ª só ADMIN (403 PAPEL_NAO_AUTORIZADO_NA_APROVACAO), `sub` distintos (409
   * APROVADOR_NAO_DISTINTO); hash diferente ⇒ 409 PREVIA_DESATUALIZADA; `versao` ⇒ CONFLITO_VERSAO.
   */
  aprovar: async (id: string, previaHash: string, versao?: number): Promise<Expurgo> =>
    api.post<Expurgo>(`${BASE}/expurgos/${encodeURIComponent(id)}/aprovar`, { previaHash, versao }),

  rejeitar: async (id: string, motivo: string): Promise<Expurgo> =>
    api.post<Expurgo>(`${BASE}/expurgos/${encodeURIComponent(id)}/rejeitar`, { motivo }),

  /** O solicitante, antes da 1ª aprovação (403 SOMENTE_SOLICITANTE_CANCELA). */
  cancelar: async (id: string): Promise<Expurgo> =>
    api.post<Expurgo>(`${BASE}/expurgos/${encodeURIComponent(id)}/cancelar`),

  /**
   * Só DPO: retira UM item de expurgo em EXECUTANDO (item em ERRO/PENDENTE que o storage nunca
   * tocou). 409 ITEM_POSSIVELMENTE_EXECUTADO / ITEM_NAO_RETIRAVEL / EXPURGO_ESTADO_INVALIDO.
   * Devolve o expurgo atualizado (todos retirados ⇒ REJEITADO).
   */
  retirarItem: async (id: string, documentoId: string, motivo: string): Promise<Expurgo> =>
    api.post<Expurgo>(
      `${BASE}/expurgos/${encodeURIComponent(id)}/itens/${encodeURIComponent(documentoId)}/retirar`,
      { motivo }
    ),

  /**
   * ADMIN: reexecuta o expurgo de documentos que voltaram de backup (idempotente, DEV-X).
   * `{verificados, reaplicados, falhas, documentos}`.
   */
  reaplicar: async (): Promise<ReaplicacaoExpurgoResponse> =>
    api.post<ReaplicacaoExpurgoResponse>(`${BASE}/expurgos/reaplicar`),

  // ---------------------------------------------------------- trilha
  registros: async (
    page = 0,
    size = 50,
    filtro: { expurgoId?: string; documentoId?: string; evento?: string } = {}
  ): Promise<PageResponse<RegistroExpurgo>> => {
    const params: Record<string, string | number> = { page, size }
    if (filtro.expurgoId) params.expurgoId = filtro.expurgoId
    if (filtro.documentoId) params.documentoId = filtro.documentoId
    if (filtro.evento) params.evento = filtro.evento
    const r = await api.get<PageResponse<RegistroExpurgo> | RegistroExpurgo[]>(`${BASE}/registros-expurgo`, {
      params,
    })
    if (Array.isArray(r)) {
      return { content: r, totalElements: r.length, totalPages: 1, number: 0, size: r.length }
    }
    return r
  },

  exportarRegistrosCsv: async (): Promise<Blob> =>
    api.get<Blob>(`${BASE}/registros-expurgo/export.csv`, { responseType: 'blob' }),
}

export default privacidadeService
